import { FileBarChart, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  reportApi,
  type ActiveAssetOption,
  type AssetOrRentalOption,
  type RentalOption,
} from '../../services/reportApi';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { useToast } from '../ui/use-toast';

/** RFC-style email validation (allows common formats). */
function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(trimmed);
}

function parseRecipientList(recipients: string): string[] {
  return recipients
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface DetailedReportData {
  name: string;
  type: string;
  schedule: string;
  description: string;
  metrics: string[];
  assets: string[];
  dateRange: string;
  recipients: string;
  includeCharts: boolean;
  format: string;
}

const defaultFormData: DetailedReportData = {
  name: '',
  type: '',
  schedule: 'On Demand',
  description: '',
  metrics: [],
  assets: [],
  dateRange: 'Last 30 Days',
  recipients: '',
  includeCharts: true,
  format: 'PDF',
};

interface DetailedReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (report: DetailedReportData) => void;
  onUpdate?: (id: string, report: DetailedReportData) => void;
  editReportId?: string;
  initialData?: DetailedReportData;
}

const reportTypes = ['Financial', 'Compliance', 'Regulatory', 'Operational', 'Investor', 'Tax'];
const schedules = ['On Demand'];
const formats = ['PDF', 'Excel', 'CSV', 'JSON'];
const dateRanges = ['Last 7 Days', 'Last 30 Days', 'Last Quarter', 'Last Year', 'Custom'];
const metricOptions = [
  'Total AUM',
  'Yield Distribution',
  'Token Holders',
  'Transaction Volume',
  'Investor Growth',
  'Compliance Status',
  'Revenue Breakdown',
  'Fee Analysis',
];
const ALL_ASSETS_LABEL = 'All Assets';

/** Selection value for formData.assets: asset name for assets, "Rental: <name>" for rentals. */
function getSelectionValue(option: AssetOrRentalOption): string {
  return option.source === 'rental' ? `Rental: ${option.assetName}` : option.assetName;
}

/** Returns 'Active' if sale end date is in the future, 'Sale ended' otherwise. */
function getSaleStatusLabel(saleEndDate?: string): string {
  if (!saleEndDate) return '—';
  return new Date(saleEndDate) > new Date() ? 'Active' : 'Sale ended';
}

export function DetailedReportModal({
  open,
  onOpenChange,
  onAdd,
  onUpdate,
  editReportId,
  initialData,
}: DetailedReportModalProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<DetailedReportData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assetOptions, setAssetOptions] = useState<ActiveAssetOption[]>([]);
  const [rentalOptions, setRentalOptions] = useState<RentalOption[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [emailInput, setEmailInput] = useState('');

  const isEditMode = !!editReportId && !!initialData;

  const recipientEmails = parseRecipientList(formData.recipients);

  const addRecipientEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address (e.g. name@example.com).',
        variant: 'destructive',
      });
      return;
    }
    if (recipientEmails.some((e) => e.toLowerCase() === trimmed.toLowerCase())) {
      toast({
        title: 'Already added',
        description: 'This email is already in the list.',
        variant: 'destructive',
      });
      return;
    }
    setFormData({
      ...formData,
      recipients: [...recipientEmails, trimmed].join(', '),
    });
    setEmailInput('');
  };

  const removeRecipientEmail = (email: string) => {
    setFormData({
      ...formData,
      recipients: recipientEmails.filter((e) => e !== email).join(', '),
    });
  };

  useEffect(() => {
    if (open) {
      setEmailInput('');
      if (initialData) {
        setFormData({
          name: initialData.name,
          type: initialData.type,
          schedule: initialData.schedule || 'On Demand',
          description: initialData.description || '',
          metrics: initialData.metrics || [],
          assets: initialData.assets || [],
          dateRange: initialData.dateRange || 'Last 30 Days',
          recipients: initialData.recipients || '',
          includeCharts: initialData.includeCharts !== false,
          format: initialData.format || 'PDF',
        });
      } else {
        setFormData(defaultFormData);
      }
    }
  }, [open, initialData]);

  // Fetch active assets and rentals when modal opens
  useEffect(() => {
    if (!open) return;
    setLoadingAssets(true);
    Promise.all([reportApi.getActiveAssets(), reportApi.getActiveRentals()])
      .then(([assets, rentals]) => {
        setAssetOptions(assets);
        setRentalOptions(rentals);
      })
      .catch(() => {
        toast({
          title: 'Could not load assets or rentals',
          description: 'Lists may be empty. You can still use "All Assets".',
          variant: 'destructive',
        });
        setAssetOptions([]);
        setRentalOptions([]);
      })
      .finally(() => setLoadingAssets(false));
  }, [open, toast]);

  const toggleMetric = (metric: string) => {
    if (formData.metrics.includes(metric)) {
      setFormData({ ...formData, metrics: formData.metrics.filter((m) => m !== metric) });
    } else {
      setFormData({ ...formData, metrics: [...formData.metrics, metric] });
    }
  };

  const toggleAsset = (assetNameOrId: string) => {
    if (assetNameOrId === ALL_ASSETS_LABEL) {
      setFormData({ ...formData, assets: [ALL_ASSETS_LABEL] });
    } else if (formData.assets.includes(assetNameOrId)) {
      setFormData({ ...formData, assets: formData.assets.filter((a) => a !== assetNameOrId) });
    } else {
      setFormData({
        ...formData,
        assets: [...formData.assets.filter((a) => a !== ALL_ASSETS_LABEL), assetNameOrId],
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.type || formData.metrics.length === 0) {
      toast({
        title: 'Validation error',
        description: 'Please fill in required fields and select at least one metric',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode && editReportId && onUpdate) {
        await onUpdate(editReportId, formData);
        onOpenChange(false);
      } else {
        await onAdd(formData);
        setFormData(defaultFormData);
        onOpenChange(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[700px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-glow text-primary-foreground'>
              <FileBarChart className='w-5 h-5' />
            </div>
            {isEditMode ? 'Edit Report' : 'Create Detailed Report'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update report configuration and metrics.'
              : 'Configure a comprehensive report with custom metrics and delivery options.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-6 py-4'>
          <div className='space-y-4'>
            <h4 className='text-sm font-semibold text-foreground uppercase tracking-wide'>
              Basic Information
            </h4>
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='name'>Report Name *</Label>
                <Input
                  id='name'
                  placeholder='e.g., Q4 Investor Summary'
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='type'>Report Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select type' />
                  </SelectTrigger>
                  <SelectContent>
                    {reportTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                placeholder='Brief description of report purpose and contents...'
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <div className='space-y-4'>
            <h4 className='text-sm font-semibold text-foreground uppercase tracking-wide'>
              Metrics to Include *
            </h4>
            <div className='grid grid-cols-2 gap-2'>
              {metricOptions.map((metric) => (
                <div
                  key={metric}
                  role='button'
                  tabIndex={0}
                  onClick={() => toggleMetric(metric)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleMetric(metric);
                    }
                  }}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    formData.metrics.includes(metric)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/30'
                  }`}
                >
                  <span onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={formData.metrics.includes(metric)}
                      onCheckedChange={() => toggleMetric(metric)}
                    />
                  </span>
                  <span className='text-sm text-foreground select-none'>{metric}</span>
                </div>
              ))}
            </div>
          </div>

          <div className='space-y-4'>
            <h4 className='text-sm font-semibold text-foreground uppercase tracking-wide'>
              Assets
            </h4>
            <p className='text-xs text-muted-foreground'>
              Select assets and/or rentals to include. Each row shows status, market type, and sale
              or rental state.
            </p>
            {loadingAssets ? (
              <div className='flex items-center gap-2 text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' />
                <span className='text-sm'>Loading assets and rentals…</span>
              </div>
            ) : (
              <div className='space-y-2 max-h-[320px] overflow-y-auto pr-1'>
                {assetOptions.map((asset) => {
                  const selectionValue = getSelectionValue(asset);
                  const isSelected = formData.assets.includes(selectionValue);
                  const saleLabel = getSaleStatusLabel(asset.saleEndDate);
                  const isSaleEnded = saleLabel === 'Sale ended';
                  return (
                    <div
                      key={asset.id}
                      role='button'
                      tabIndex={0}
                      onClick={() => toggleAsset(selectionValue)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleAsset(selectionValue);
                        }
                      }}
                      className={`flex flex-wrap items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/10 ring-1 ring-primary/20'
                          : 'border-border hover:border-primary/30 hover:bg-muted/50'
                      }`}
                    >
                      <div className='flex-1 min-w-0'>
                        <p className='font-medium text-foreground truncate'>{asset.assetName}</p>
                        {asset.symbol && (
                          <p className='text-xs text-muted-foreground'>{asset.symbol}</p>
                        )}
                      </div>
                      <div className='flex flex-wrap items-center gap-1.5'>
                        {asset.status && (
                          <Badge
                            variant={
                              asset.status === 'APPROVED'
                                ? 'default'
                                : asset.status === 'PENDING'
                                  ? 'secondary'
                                  : 'destructive'
                            }
                            className='text-xs font-normal'
                          >
                            {asset.status}
                          </Badge>
                        )}
                        <Badge variant='outline' className='text-xs font-normal'>
                          {asset.isListedForResell ? 'Secondary' : 'Primary'}
                        </Badge>
                        {!asset.isListedForResell && (
                          <Badge
                            variant={isSaleEnded ? 'secondary' : 'outline'}
                            className={`text-xs font-normal ${!isSaleEnded ? 'border-green-500/50 text-green-700 dark:text-green-400' : ''}`}
                          >
                            {saleLabel}
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
                {rentalOptions.length > 0 && (
                  <>
                    <p className='text-xs font-medium text-muted-foreground pt-2 pb-1'>Rentals</p>
                    {rentalOptions.map((rental) => {
                      const selectionValue = getSelectionValue(rental);
                      const isSelected = formData.assets.includes(selectionValue);
                      return (
                        <div
                          key={rental.id}
                          role='button'
                          tabIndex={0}
                          onClick={() => toggleAsset(selectionValue)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleAsset(selectionValue);
                            }
                          }}
                          className={`flex flex-wrap items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-primary bg-primary/10 ring-1 ring-primary/20'
                              : 'border-border hover:border-primary/30 hover:bg-muted/50'
                          }`}
                        >
                          <div className='flex-1 min-w-0'>
                            <p className='font-medium text-foreground truncate'>
                              {rental.assetName}
                            </p>
                            <p className='text-xs text-muted-foreground'>Rental listing</p>
                          </div>
                          <div className='flex flex-wrap items-center gap-1.5'>
                            <Badge className='text-xs font-normal bg-violet-500/20 text-violet-700 dark:text-violet-300 border-violet-500/30'>
                              Rental
                            </Badge>
                            {rental.status && (
                              <Badge
                                variant={rental.status === 'AVAILABLE' ? 'default' : 'secondary'}
                                className='text-xs font-normal'
                              >
                                {rental.status}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                <button
                  type='button'
                  onClick={() => toggleAsset(ALL_ASSETS_LABEL)}
                  className={`flex items-center gap-2 w-full p-3 rounded-lg border text-left font-medium transition-all ${
                    formData.assets.includes(ALL_ASSETS_LABEL)
                      ? 'border-primary bg-primary/10 ring-1 ring-primary/20 text-primary'
                      : 'border-border hover:border-primary/30 hover:bg-muted/50 text-foreground'
                  }`}
                >
                  {ALL_ASSETS_LABEL}
                </button>
              </div>
            )}
          </div>

          <div className='space-y-4'>
            <h4 className='text-sm font-semibold text-foreground uppercase tracking-wide'>
              Configuration
            </h4>
            <div className='grid grid-cols-3 gap-4'>
              <div className='space-y-2'>
                <Label>Date Range</Label>
                <Select
                  value={formData.dateRange}
                  onValueChange={(value) => setFormData({ ...formData, dateRange: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dateRanges.map((range) => (
                      <SelectItem key={range} value={range}>
                        {range}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-2'>
                <Label>Schedule</Label>
                <Select
                  value={formData.schedule}
                  onValueChange={(value) => setFormData({ ...formData, schedule: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {schedules.map((schedule) => (
                      <SelectItem key={schedule} value={schedule}>
                        {schedule}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-2'>
                <Label>Format</Label>
                <Select
                  value={formData.format}
                  onValueChange={(value) => setFormData({ ...formData, format: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {formats.map((format) => (
                      <SelectItem key={format} value={format}>
                        {format}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='recipients'>Email Recipients</Label>
              <p className='text-xs text-muted-foreground'>
                Add valid email addresses; invalid or duplicate entries are rejected.
              </p>
              {recipientEmails.length > 0 && (
                <div className='flex flex-wrap gap-2'>
                  {recipientEmails.map((email) => (
                    <Badge
                      key={email}
                      variant='secondary'
                      className='pl-2 pr-1 py-1 gap-1 font-normal'
                    >
                      {email}
                      <button
                        type='button'
                        aria-label={`Remove ${email}`}
                        className='rounded-full hover:bg-muted p-0.5'
                        onClick={() => removeRecipientEmail(email)}
                      >
                        <X className='h-3 w-3' />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <div className='flex gap-2'>
                <Input
                  id='recipients'
                  type='email'
                  placeholder='name@example.com'
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addRecipientEmail();
                    }
                  }}
                />
                <Button
                  type='button'
                  variant='outline'
                  onClick={addRecipientEmail}
                  disabled={!emailInput.trim()}
                >
                  Add
                </Button>
              </div>
            </div>

            <div className='flex items-center gap-3 p-3 rounded-lg border border-border'>
              <Checkbox
                id='includeCharts'
                checked={formData.includeCharts}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, includeCharts: !!checked })
                }
              />
              <Label htmlFor='includeCharts' className='cursor-pointer'>
                Include visual charts and graphs in report
              </Label>
            </div>
          </div>

          <DialogFooter className='pt-4'>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              {isSubmitting
                ? isEditMode
                  ? 'Updating...'
                  : 'Creating...'
                : isEditMode
                  ? 'Update Report'
                  : 'Create Report'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
